import { useEditor, useNode } from '@craftjs/core';
import { expressionFunctions } from '@lmt/expressions/src/expressions/helpers/expression-functions';
import { DataType, MergeFieldQueryType, QueryFactory } from '@lmt/shared-types';

import { MonacoExpressionBuilder } from '@lmt/unicorn-shared/app/common/Monaco/MonacoExpressionBuilder';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { Input as CommonInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { RadioButtonGroup } from '@lmt/unicorn-shared/app/components/common/ui-kit/RadioButtonGroup';
import {
  getDataBlockFunctionDeclarations,
  getFunctionDeclarations,
} from '@lmt/unicorn-shared/app/components/documents/components/intellisense-helper';
import { CheckboxSettings } from '@lmt/unicorn-shared/app/components/editor/components/blocks/CheckboxSettings';
import { useLocalizeDataTypes } from '@lmt/unicorn-shared/app/components/measurement-fields/common';
import { useDataBlockConfigToolbarParams } from '@lmt/unicorn-shared/app/components/measurements/ToolbarComponent';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';

import styles from '../UIEditorBlocks.module.scss';
import { WidthSelectControl } from '../WidthSelectControl';
import { DictionaryType, InputProps } from './types';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import { InputValuesContext } from '../../../UIEditorTypes';

export const InputSettings = () => {
  const { actions, props } = useNode((node) => ({
    props: node.data.props as InputProps,
  }));

  const { query } = useEditor();

  const { setProp } = actions;
  const i18next = useScopedTranslation('editor.settings');
  const i18n = useTranslation();
  const modals = useModal();
  const queryClient = useQueryClient();
  const [queryParams] = useDataBlockConfigToolbarParams();

  const [label, setLabel] = useState<string>(props.label);
  const [format, setFormat] = useState<string>(props.format);
  const [allowEditableChange, setAllowEditableChange] = useState<boolean>(
    !props.expression,
  );

  async function onDictionaryTypeChange(value: any) {
    const isConfirmed = await modals.showConfirm({
      title: i18next.t('confirm_change_visit_type_title'),
      text: i18next.t('confirm_change_visit_type_text'),
      okButtonText: i18next.t('confirm_change_visit_type_button'),
    });
    if (isConfirmed) {
      setProp((props: InputProps) => (props.dictionaryType = value?.value));
    }
  }

  async function convertTextFieldToDictionary() {
    if (query.history.canUndo()) {
      await modals.showAlert({
        text: i18next.t('confirm_message_copy_config_should_save'),
      });
      return;
    }

    const dataBlockTypeId = queryParams.dataBlockTypeId;
    const name = await modals.showPrompt({
      title: i18next.t('confirm_convert_text_field_to_dictionary_title'),
      text: i18next.t('confirm_convert_text_field_to_dictionary_text'),
      okButtonText: i18next.t(
        'confirm_convert_text_field_to_dictionary_button',
      ),
      defaultValue: '',
      isMandatory: true,
    });

    if (name) {
      try {
        // convert to dictionary
        await QueryFactory.DataBlockFieldClient.convertFieldToDictionary(
          props.dataBlockFieldId,
          { dictionaryTitle: name },
        );

        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockTypeQuery.getUIConfigsQueryKey(
            dataBlockTypeId!,
          ),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockDictionaryQuery.getQueryKey(),
        });
      } catch (error) {
        await modals.showError({ text: parseError(error) });
      }
    }
  }

  const dataBlockTypeQuery = QueryFactory.DataBlockTypeQuery.useGetQuery();
  const mergeFieldQuery = QueryFactory.MergeFieldQuery.useGetQueriesQuery();

  const intellisenseContent = useMemo(() => {
    if (!dataBlockTypeQuery.data || !mergeFieldQuery.data) return '';
    const dataBlockTypes = dataBlockTypeQuery.data ?? [];
    const documentQueries = mergeFieldQuery.data ?? [];

    return [
      ...expressionFunctions.map((x) => `declare function ${x.signature};`),
      getFunctionDeclarations(
        ['patient'],
        'PatientType',
        documentQueries,
        MergeFieldQueryType.PatientQuery,
      ),
      getFunctionDeclarations(
        ['visit'],
        'VisitType',
        documentQueries,
        MergeFieldQueryType.VisitQuery,
      ),
      getFunctionDeclarations(
        ['Case'],
        'CaseType',
        documentQueries,
        MergeFieldQueryType.CaseQuery,
      ),
      getDataBlockFunctionDeclarations(
        ['block'],
        documentQueries,
        dataBlockTypes,
      ),
    ].join('\n');
  }, [dataBlockTypeQuery.data, mergeFieldQuery.data]);

  const filteredDictionaryTypes: DictionaryType[] = useMemo(() => {
    if (props.dataType === DataType.Dictionary) {
      const dictionaryTypes: DictionaryType[] = [];

      if (props.isOptionAllowed) {
        dictionaryTypes.push(DictionaryType.Combobox);
        dictionaryTypes.push(DictionaryType.RadioGroupWithCustomOption);
      } else {
        dictionaryTypes.push(DictionaryType.DropDown);
        dictionaryTypes.push(DictionaryType.RadioGroup);
      }
      if (!props.isSingleValue) {
        if (props.isOptionAllowed) {
          dictionaryTypes.push(DictionaryType.CheckboxGroupWithCustomOption);
        } else {
          dictionaryTypes.push(DictionaryType.CheckboxGroup);
        }
      }
      return dictionaryTypes;
    } else return [];
  }, []);

  const formatDisableReason =
    props.dataType !== DataType.Number &&
    props.dataType !== DataType.TimeOnly &&
    props.dataType !== DataType.DateOnly &&
    props.dataType !== DataType.DateTime &&
    i18next.t('format_hint');

  const isFormatDisabled = !!formatDisableReason;
  const formatHint =
    props.dataType === DataType.Number ? (
      i18next.t('format_number_hint')
    ) : props.dataType === DataType.TimeOnly ? (
      i18next.t('format_timeonly_hint')
    ) : props.dataType === DataType.DateTime ||
      props.dataType === DataType.DateOnly ? (
      <UniLink
        target="_blank"
        color={ButtonColor.Primary}
        title={i18next.t('format_datetime_hint')}
        href="https://date-fns.org/v2.30.0/docs/format"
      />
    ) : undefined;
  const localizeDataTypes = useLocalizeDataTypes();
  const allowMultiline =
    props.dataType == DataType.Text ||
    (props.dataType == DataType.Dictionary &&
      props.dictionaryType &&
      props.isOptionAllowed &&
      [
        DictionaryType.RadioGroupWithCustomOption,
        DictionaryType.CheckboxGroupWithCustomOption,
      ].includes(props.dictionaryType));

  const dataBlockTypeName = useContextSelector(
    InputValuesContext,
    (v) => v.dataBlockTypeName,
  );

  const fieldLabel = props.isShared
    ? i18n.t(
        'data-block-fields:' +
          dataBlockTypeName.split('.', 1)[0] +
          '.shared' +
          '.' +
          props.dataKey.toLowerCase(),
        props.dataKey,
      )
    : i18n.t(
        'data-block-fields:' +
          dataBlockTypeName +
          '.' +
          props.dataKey.toLowerCase(),
        props.dataKey,
      );

  return (
    <div className={styles.inputSettings}>
      <div className={styles.header}>
        {i18next.t('field', { label: fieldLabel })} (
        {localizeDataTypes[props.dataType]})
      </div>
      <CheckboxSettings
        label={i18next.t('editable')}
        isChecked={props.isEditable}
        disabled={!allowEditableChange}
        setValue={(newValue) =>
          setProp((props: InputProps) => (props.isEditable = newValue))
        }
      />
      {props.dataType !== DataType.Table &&
        props.dataType !== DataType.Boolean && (
          <CheckboxSettings
            label={i18next.t('mandatory')}
            isChecked={props.isRequired ?? props.isMandatory}
            setValue={(newValue) => {
              setProp((props: InputProps) => {
                props.isMandatory = newValue;
                if (newValue) {
                  props.isHiddenWhenEmpty = false;
                }
              });
            }}
            disabled={props.isRequired}
          />
        )}
      {props.dataType !== DataType.Boolean && (
        <CheckboxSettings
          label={i18next.t('hide_when_empty')}
          isChecked={!props.isMandatory && (props.isHiddenWhenEmpty ?? false)}
          disabled={props.isRequired ?? props.isMandatory}
          setValue={(newValue) =>
            setProp((props: InputProps) => (props.isHiddenWhenEmpty = newValue))
          }
        />
      )}

      {allowMultiline && (
        <CheckboxSettings
          label={i18next.t('multiline')}
          isChecked={props.isMultiline}
          setValue={(newValue) =>
            setProp((props: InputProps) => (props.isMultiline = newValue))
          }
        />
      )}
      {props.dataType == DataType.Boolean ? (
        <Field title={i18next.t('label')}>
          <CommonInput
            value={label}
            testId={'uieditor-field-label'}
            onChange={(e) => {
              setLabel(e.target?.value);
              setProp((props: InputProps) => (props.label = e.target?.value));
            }}
          />
        </Field>
      ) : (
        <>
          <CheckboxSettings
            label={i18next.t('has_label')}
            isChecked={props.isLabel}
            setValue={(newValue) =>
              setProp((props: InputProps) => (props.isLabel = newValue))
            }
          />
          {props.isLabel && (
            <CommonInput
              value={label}
              testId={'uieditor-field-label'}
              onChange={(e) => {
                setLabel(e.target?.value);
                setProp((props: InputProps) => (props.label = e.target?.value));
              }}
            />
          )}
        </>
      )}

      {props.dataType === DataType.Dictionary && (
        <div>
          <CheckboxSettings
            label={i18next.t('dictionary_item_adding')}
            isChecked={props.isDictionaryItemAdding ?? false}
            setValue={(newValue) =>
              setProp(
                (props: InputProps) =>
                  (props.isDictionaryItemAdding = newValue),
              )
            }
          />
          <div style={{ paddingTop: '12px' }}>
            <Field title={i18next.t('dictionary_type')}>
              <RadioButtonGroup
                required={true}
                labelField="displayValue"
                valueField="value"
                options={filteredDictionaryTypes.map((type) => {
                  return {
                    value: type,
                    displayValue: i18next.t(
                      `dictionary_type_${type.toString()}` as any,
                    ),
                  };
                })}
                value={props.dictionaryType}
                onSelectedOptionChanged={(value) =>
                  onDictionaryTypeChange(value)
                }
                radioButtonClassName={styles.radioButton}
              />
            </Field>
          </div>

          {props.dictionaryType !== DictionaryType.Combobox && (
            <Field title={i18next.t('options_number')}>
              <CommonInput
                value={props.visibleOptionsNumber}
                onChange={(e) => {
                  setProp(
                    (props: InputProps) =>
                      (props.visibleOptionsNumber = Number(e.target?.value)),
                  );
                }}
              />
            </Field>
          )}
        </div>
      )}
      <WidthSelectControl props={props} setProp={setProp} />
      <div style={{ height: '10px' }} />
      {props.dataType !== DataType.Table &&
        props.dataType !== DataType.Dictionary && (
          <Field
            title={i18next.t('format')}
            hint={
              (isFormatDisabled ? formatDisableReason : formatHint) || undefined
            }
            className={styles['settings-field']}
          >
            <CommonInput
              value={format}
              disabled={isFormatDisabled}
              onChange={(e) => {
                setFormat(e.target?.value);
                setProp(
                  (props: InputProps) => (props.format = e.target?.value),
                );
              }}
              className={styles.formatInput}
            />
          </Field>
        )}

      {props.dataType !== DataType.Dictionary && (
        <Field
          title={i18next.t('expression')}
          testId="ui-editor-calculated-field"
          className={styles['settings-field']}
        >
          <MonacoExpressionBuilder
            defaultExpressionValue={props.expression}
            onExpressionChanged={(e) => {
              setAllowEditableChange(!e);
              setProp(
                (props: InputProps) => (
                  (props.expression = e),
                  (props.isEditable = !e)
                ),
              );
            }}
            intellisenseContent={intellisenseContent}
          />
        </Field>
      )}
      {props.dataType === DataType.Text && !props.isShared && (
        <Button
          title={i18next.t('convert_to_dictionary')}
          color={ButtonColor.Primary}
          className={styles.convertButton}
          onClick={() => convertTextFieldToDictionary()}
        />
      )}
    </div>
  );
};
